<template>
  <div
     class="nuttrema-iconica-kcal"
    :class="[className.kcal, className.haveImage]"
    :style="style.main"
    :title="title">
    <span class="txt">
      <span :style="style.fontSizeText">{{ text | first-two-upper }}</span>
    </span>
    <span class="kcal-info">
      <span class="tbl">
        <span class="tc">
          <span class="value" :style="style.fontSizeValue">{{ enercKcal }}</span>
          <span class="description" :style="style.fontSizeDescription">kcal</span>
        </span>
      </span>
    </span>
    <span class="circle" :style="style.circle"></span>
    <span v-if="image" class="image" :style="style.image"></span>
  </div>
</template>

<script>
import { isNil, isArray, isObject, has, get } from 'lodash';

export default {
  name: 'nuttrema-iconica-kcal',
  props: {
    size: {
      type: String,
      required: false,
      default: 'normal',
    },
    text: {
      required: false,
      default: '',
    },
    title: {
      required: false,
      default: null,
    },
    image: {
      type: String,
      required: false,
      default: null,
    },
    nutrientDefinition: {
      required: false,
      default: null,
    },
    energyKcal: {
      required: false,
      default: null,
    },
  },
  data() {
    return {
      sizes: {
        small: {
          width: '1.85em',
          fontSize: '1.15rem',
        },
        normal: {
          width: '2.5em',
          margin: '0.5em',
          fontSize: {
            text: '1rem',
            value: '0.9rem',
            description: '0.8rem',
          },
          circle: {
            width: '3.5em',
            top: '-0.5em',
            left: '-0.5em',
          },
        },
        large: {
          width: '3.1em',
          fontSize: '1.4rem',
        },
      },
    };
  },
  methods: {
    calculateKcalScale(value, prefix) {
      prefix = prefix || 'kcal-scale-level-';
      value = value ? Number(value) : 0;
      let sufix = 'nil';
      switch (true) {
        case (value > 0 && value < 50):
          sufix = '0';
          break;
        case (value >= 50 && value < 100):
          sufix = '1';
          break;
        case (value >= 100 && value < 200):
          sufix = '2';
          break;
        case (value >= 200 && value < 300):
          sufix = '3';
          break;
        case (value >= 300 && value < 500):
          sufix = '4';
          break;
        case (value >= 500):
          sufix = '5';
          break;
        default:
          break;
      }
      return prefix + sufix;
    },
  },
  computed: {
    style() {
      const size = this.sizes[this.size];
      return {
        main: `width: ${size.width}; max-width: ${size.width}; height: ${size.width}; max-height: ${size.width}; margin: ${size.margin}`,
        fontSizeText: has(size, 'fontSize.text') ? `font-size: ${size.fontSize.text};` : '',
        fontSizeValue: has(size, 'fontSize.value') ? `font-size: ${size.fontSize.value};` : '',
        fontSizeDescription: has(size, 'fontSize.description') ? `font-size: ${size.fontSize.description};` : '',
        circle: `width: ${size.circle.width}; max-width: ${size.circle.width}; height: ${size.circle.width}; max-height: ${size.circle.width}; left: ${size.circle.left}; top: ${size.circle.top}`,
        image: `background-image: url(${this.image})`,
      };
    },
    className() {
      return {
        kcal: this.calculateKcalScale(this.enercKcal),
        haveimage: isNil(this.image) ? 'no-image' : '',
      };
    },
    enercKcal() {
      if (!isNil(this.energyKcal) && this.energyKcal !== 0) return Math.round(this.energyKcal);
      if (isNil(this.nutrientDefinition)) return 0;
      let nutDef;
      let nutritionValue = 0;

      if (isArray(this.nutrientDefinition)) {
        nutDef = this.nutrientDefinition.find(item => item.code === 'ENERC_KCAL');
        if (nutDef) {
          nutritionValue = nutDef ? Math.round(nutDef.NutritionInformation.nutrition_value) : 0;
        } else {
          const nutrients = this.nutrientDefinition.find(item => item.group === 'nutrients');
          const nutGroup = has(nutrients, 'items') ? nutrients.items.find(item => item.group_code === 'ENERC') : false;
          if (nutGroup) {
            nutDef = nutGroup.values.find(item => item.code === 'ENERC_KCAL');
            nutritionValue = nutDef ? Math.round(nutDef.values[0].quantity) : 0;
          }
        }
      } else if (has(this.nutrientDefinition, 'ENERC_KCAL')) {
        nutDef = get(this.nutrientDefinition, 'ENERC_KCAL', 0);
        nutritionValue = isObject(nutDef) ? Math.round(nutDef.quantity) : Math.round(nutDef);
      } else if (has(this.nutrientDefinition, 'energy_kcal_total')) {
        nutDef = this.nutrientDefinition.energy_kcal_total;
        nutritionValue = nutDef ? Math.round(nutDef) : 0;
      }

      return nutritionValue;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~styles/mixins/mixins";
  @import "~styles/theme/colors";
  .nuttrema-iconica-kcal, .nuttrema-dot-kcal {
    display: block;
    position: relative;
    border-radius: 50%;
    background-color: darken($turquoise, 5%);

    span.txt {
        display: table;
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0; left: 0;
        vertical-align: middle;
        text-align: center;
        z-index: 1;
        color: #fff;
        > span, > i {
            display: table-cell;
            vertical-align: middle;
            font-size: 1.25rem;
        }
        > i {
            font-size: 24px;
        }
    }

    span.image {
      display: inline-block;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0; left: 0;
      border-radius: 50%;
      background-image: none;
      background-size: 100% auto;
      background-position: center center;
      background-repeat: no-repeat;
      z-index: 2;
      @include insetShadow(rgba(0, 0, 0, 0.75), 0px 3px 3px -4px);
    }

    span.txt {
      > span {
        font-size: 1rem;
        font-weight: 600;
        text-shadow: 0 -1px 0 rgba(0,0,0,0.25);
      }
    }

    span.kcal-info {
      display: none;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      z-index: 1;
      background-color: rgba(0,0,0,0.5);
      left: 0; top: 0;
      > span.tbl {
        display: table;
        vertical-align: middle;
        width: 100%;
        height: 100%;
        position: absolute;
        color: #fff;
        > span.tc {
          display: table-cell;
          vertical-align: middle;
          text-align: center;
          font-weight: 800;
          text-shadow: 0 1px solid rgba(0,0,0,0.75);
          line-height: 12px;
          > span {
            display: block;
            &.value {
                font-size: 1rem;
                line-height: 1;
            }
            &.description {
                font-weight: 400;
                font-size: 0.9rem;
            }
          }
        }
      }
    }

    span.circle {
      z-index: 3;
      opacity: 1;
      content: '';
      border-radius: 50%;
      position: absolute;
      display: inline-block;
      transition: all 0.5s linear;
      -o-transition: all 0.5s linear;
      -moz-transition: all 0.5s linear;
      -webkit-transition: all 0.5s linear;
      border: 4px solid darken($turquoise, 5%);
    }

    span.dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background-color: blue;
    }

    &:hover, &:active, &:focus {
      cursor: pointer;
      span.circle { opacity: 1; }
      span.image { z-index: 1; }
      span.txt { display: none; }
      span.kcal-info {
          z-index: 2;
          display: inline-block;
      }
    }

    &.no-image {
      span.image {
        opacity: 0;
      }
    }

    &.no-hover {
      &:hover, &:active, &:focus {
        span.circle { opacity: 0; }
      }
    }

    .is-active & {
      background-color: darken($turquoise, 10%);
      span.circle {
        opacity: 1;
        border-color: darken($turquoise, 10%);
      }
    }

    &.kcal-scale-level-5 {
      background-color: darken(#FE6666, 10%);
      span.circle {
        border-color: #FE6666 !important;
      }
      span.dot {
        background-color: darken(#FE6666, 10%);
      }
    }

    &.kcal-scale-level-4 {
      background-color: darken(#E08566, 10%);
      span.circle {
        border-color: #E08566 !important;
      }
      span.dot {
        background-color: darken(#E08566, 10%);
      }
    }

    &.kcal-scale-level-3 {
      background-color: darken(#E09866, 10%);
      span.circle {
        border-color: #E09866 !important;
      }
      span.dot {
        background-color: darken(#E09866, 10%);
      }
    }

    &.kcal-scale-level-2 {
      background-color: darken(#A3C266, 10%);
      span.circle {
        border-color: #A3C266 !important;
      }
      span.dot {
        background-color: darken(#A3C266, 10%);
      }
    }

    &.kcal-scale-level-1 {
      background-color: darken(#86D186, 10%);
      span.circle {
        border-color: #86D186 !important;
      }
      span.dot {
        background-color: darken(#86D186, 10%);
      }
    }

    &.kcal-scale-level-0 {
      background-color: darken(#85E066, 10%);
      span.circle {
        border-color: #85E066 !important;
      }
      span.dot {
        background-color: darken(#85E066, 10%);
      }
    }

    &.kcal-scale-level-nil {
      background-color: darken($gray-lighter, 10%);
      span.circle {
        border-color: $gray-lighter !important;
      }
      span.dot {
        background-color: darken($gray-lighter, 10%);
      }
      .txt {
        color: $gray-light;
        > span {
          text-shadow: 0 -1px 0 rgba(255,255,255,0.5);
        }
      }
    }
  }

  .nuttrema-dot-kcal {
    display: flex;
    min-width: 15px;
    justify-content: center;
    background-color: transparent !important;
    padding: 5px 0;

    &.is-medium {
      padding: 4px;
      min-width: 12px;
      span.dot {
        width: 12px;
        height: 12px;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/ui/label/nuttrema-iconica-kcal.vue